import {
  METHODOLOGY_THEME_PARAM,
  type MethodologyTheme,
  PagePaths,
  WILSON_CLIENT_NAMES,
} from '@revelio/core';

const DATA_DICTIONARY_ORIGIN = 'https://www.data-dictionary.reveliolabs.com';
/** `make livehtml` / `python -m http.server 45000` in the data-dictionary repo. */
const DATA_DICTIONARY_LOCAL_ORIGIN = 'http://localhost:45000';

/** The site the page embeds: the local build when the dev switch is on. */
export function resolveDataDictionaryOrigin(): string {
  return process.env.PUBLIC_LOCAL_DATA_DICTIONARY === 'true'
    ? DATA_DICTIONARY_LOCAL_ORIGIN
    : DATA_DICTIONARY_ORIGIN;
}

const DATA_DICTIONARY_BRAND_PARAM = 'brnd';
const DATA_DICTIONARY_WILSON_BRAND = 'wilson';
/** Params the dashboard injects into the iframe URL; the site echoes them back. */
const DASHBOARD_OWNED_PARAMS = [
  DATA_DICTIONARY_BRAND_PARAM,
  METHODOLOGY_THEME_PARAM,
];

/**
 * The query string the dashboard route should carry when the site reports its
 * location: everything the site had, minus the params the dashboard put there.
 */
function toDashboardSearch(search: string | undefined): string {
  const params = new URLSearchParams(search ?? '');
  for (const name of DASHBOARD_OWNED_PARAMS) {
    params.delete(name);
  }
  const query = params.toString();
  return query ? `?${query}` : '';
}

/**
 * The dashboard route for a location the site reported, e.g. path
 * `data/transitions` with search `?theme=dark&q=salary` becomes
 * `/data-dictionary/data/transitions?q=salary`.
 */
export function toDashboardRoute(
  path: string,
  search: string | undefined
): string {
  const relative = path.startsWith('/') ? path.slice(1) : path;
  return `/${PagePaths.DATA_DICTIONARY}/${relative}${toDashboardSearch(search)}`;
}

type BuildDataDictionaryIframeUrlArgs = {
  /** Dashboard `location.pathname`, e.g. `/data-dictionary/data/transitions`. */
  pathname: string;
  /** Dashboard `location.search`, passed through to the site. */
  search: string;
  /** First load only; the site keeps both afterwards. */
  clientName?: string;
  theme?: MethodologyTheme;
  origin?: string;
};

/**
 * Sphinx pages are `<category>.html` with `#<anchor>` sections; the dashboard
 * exposes them as `/data-dictionary/<category>/<anchor>`.
 */
function toSitePath(pathname: string): { path: string; hash: string } {
  const suffix = pathname.replace(`/${PagePaths.DATA_DICTIONARY}`, '');
  const nested = suffix.match(/^(\/[^/]+)\/([^/]+)(.*)$/);
  if (nested) {
    const [, category, anchor, rest] = nested;
    return { path: `${category}.html`, hash: `#${anchor}${rest}` };
  }
  if (/^\/[^/]+$/.test(suffix)) {
    return { path: `${suffix}.html`, hash: '' };
  }
  return { path: '/', hash: '' };
}

export function buildDataDictionaryIframeUrl({
  pathname,
  search,
  clientName,
  theme,
  origin = DATA_DICTIONARY_ORIGIN,
}: BuildDataDictionaryIframeUrlArgs): string {
  const { path, hash } = toSitePath(pathname);
  const base = new URL(origin);
  const target = new URL(path, base);
  // A segment starting with `\` or `//` parses as a protocol-relative
  // reference and would move the iframe off the site; stay on its root.
  const escaped = target.origin !== base.origin;
  const url = escaped ? new URL('/', base) : target;
  const params = new URLSearchParams(search);
  if (clientName && WILSON_CLIENT_NAMES.has(clientName)) {
    params.set(DATA_DICTIONARY_BRAND_PARAM, DATA_DICTIONARY_WILSON_BRAND);
  }
  if (theme) {
    params.set(METHODOLOGY_THEME_PARAM, theme);
  }
  url.search = params.toString();
  url.hash = escaped ? '' : hash;
  return url.toString();
}
